import React, {useId} from "react";
import TextInput from "../TextInput";
import Icon from "../Icon";
import styles from "./AddressSearch.module.scss";
import { ResultsPanel } from "./ResultsPanel";
import { useAddressSearch } from "./useAddressSearch";
import Typography from "../Typography";
import { useTranslation } from "react-i18next";

export type GroupSuggestion = {
  id: string;
  code: string;
  label: string;
  count?: number;
};

export type DetailSuggestion = {
  id: string;
  label: string;
  lines?: string[];
};

export type AddressSearchStrings = {
  placeholder: string;
  clearAriaLabel: string;
  listAriaLabel: string;
  enterManually: string;
  notListed: string;
  emptyResults: string;
  addressesSuffix: string; 
  summaryTitle: string;
  searchLabel: string;                 
  searchHint: string;      
  externalCollectionLabel: string;     
  externalCollectionPlaceholder: string; 
};

export const makeDefaultStrings = (t: (k: string) => string): AddressSearchStrings => ({
   placeholder: t("address_search_type_address"),      
  clearAriaLabel: t("address_search_clear"),           
  listAriaLabel: t("address_search_suggestions"),     
  enterManually: t("address_search_enter_manual"),      
  notListed: t("address_search_not_listed"),               
  emptyResults: t("address_search_no_addresses"),          
  addressesSuffix: t("address_search_addresses"),    
  summaryTitle: t("address_search_collection_address"),         
  searchLabel: t("address_search_find"),               
  searchHint: t("address_search_address_postcode_town"),          
  externalCollectionLabel: t("address_search_collection_address"),  
  externalCollectionPlaceholder: t("address_search_type_collection_address") 
});

export type AddressSearchProps = {
  id?: string;
  value: string;
  label?: string;
  subLabel?: string;
  disabled?: boolean;
  className?: string;
  ariaDescribedBy?: string;
  formatSelectedLabel?: (label: string, detail: DetailSuggestion) => string;
  onChange: (next: string) => void;
  onClear: () => void;
  fetchGroupSuggestions: (q: string) => Promise<GroupSuggestion[]>;
  fetchDetailSuggestions: (groupId: string) => Promise<DetailSuggestion[]>;
  onSelectGroup?: (g: GroupSuggestion) => void;
  onSelectDetail: (d: DetailSuggestion) => void;
  onEnterManual?: () => void;
  debounceMs?: number;         // default 300
  minChars?: number;           // default 2
  keepPostcodeOnSelect?: boolean; // default true
  showSummaryCard?: boolean;   // default false
  manualValue?: string;
  onManualChange?: (v: string) => void;
  onFormattedSelect?: (text: string, detail: DetailSuggestion) => void;
  strings?: Partial<AddressSearchStrings>;
  placeholder?: string;    
  suppressFirstOpen?: boolean;    
  allowDropdown?: boolean;   
};

/**
 * AddressSearch
 *
 * Controlled address autocomplete that shows group and detail suggestions,
 * supports keyboard navigation, and can hand off to manual entry.
 *
 * Note: “Collection address” display is expected to live outside this component.
 * Use `onEnterManual` to show an external textarea and `onFormattedSelect` to seed it.
 *
 * @param id Optional base id for the input; also used to derive internal element ids.
 * @param value Controlled value of the search input.
 * @param label Label text rendered for the search input.
 * @param subLabel Hint text rendered under the input.
 * @param disabled Disables the input and interactions.
 * @param className Optional class name for the root wrapper.
 * @param ariaDescribedBy Id referenced by `aria-describedby` on the input (external hint/help).
 * @param formatSelectedLabel Custom formatter: `(label, detail) => multiline text` for a selected detail.
 * @param onChange Called with the next input value when the user types.
 * @param onClear Called when the clear action is triggered; also resets internal state.
 * @param fetchGroupSuggestions Async fetch of group suggestions for a query.
 * @param fetchDetailSuggestions Async fetch of detail suggestions for a given group id.
 * @param onSelectGroup Called when a group row is selected.
 * @param onSelectDetail Called when a detail row is selected (required).
 * @param onEnterManual If provided, shows “Enter manually”; called when the user opts for manual entry.
 * @param debounceMs Debounce (ms) for group queries. Default 300.
 * @param minChars Minimum normalized characters before querying. Default 2.
 * @param keepPostcodeOnSelect Keep the group code/postcode in the input after selecting a detail. Default true.
 * @param showSummaryCard Render the internal summary textarea below the control. Default false.
 * @param manualValue Controlled value for the internal summary textarea (when `showSummaryCard` is true).
 * @param onManualChange Controlled setter for `manualValue`.
 * @param onFormattedSelect Called with the formatted multiline text and detail after selection.
 * @param strings i18n overrides merged with `DEFAULT_STRINGS` (placeholder, labels, footer text, etc.).
 * @param placeholder (Deprecated) Custom placeholder; prefer `strings.placeholder`.
 */
export default function AddressSearch(props: AddressSearchProps) {
  const {
    id:idProp,
    value,
    label,
    subLabel,
    disabled,
    className,
    onChange,
    onClear,
    fetchGroupSuggestions,
    fetchDetailSuggestions,
    onSelectGroup,
    onSelectDetail,
    onEnterManual,
    debounceMs = 300,
    minChars = 2,
    keepPostcodeOnSelect = true,
    showSummaryCard = false,
    manualValue,
    onManualChange,
    ariaDescribedBy,
    formatSelectedLabel,
    onFormattedSelect,
    strings: stringsOverrides,
    placeholder,
    suppressFirstOpen = false,
    allowDropdown = true,
  } = props;

  const { t, } = useTranslation();
  const [allowOpen, setAllowOpen] = React.useState(!suppressFirstOpen);
  const unlock = React.useCallback(() => setAllowOpen(true), []);
  const reactId = useId();
  const baseId = idProp ?? `address-search-${reactId}`;

   React.useEffect(() => {
    if (!allowDropdown) {
      setAllowOpen(false);
    }
  }, [allowDropdown]);

  const defaultStrings = React.useMemo(
    () => makeDefaultStrings(t),
    [t,]
  );

  const strings = React.useMemo(
    () => ({ ...defaultStrings, ...stringsOverrides }),
    [defaultStrings, stringsOverrides]
  );
  
  const addressSearch = useAddressSearch({
    value,
    onChange,
    onClear,
    fetchGroupSuggestions,
    fetchDetailSuggestions,
    onSelectGroup,
    onSelectDetail,
    onEnterManual,
    debounceMs,
    minChars,
    keepPostcodeOnSelect,
    showSummaryCard,
    manualValue,
    onManualChange,
    formatSelectedLabel,
    onFormattedSelect
  });

  React.useEffect(() => {
    if (showSummaryCard && manualValue && manualValue.trim() && !addressSearch.showSummary) {
      addressSearch.enterManual();
    }
  }, [showSummaryCard, manualValue, addressSearch.showSummary]);

  React.useEffect(() => {
    if (!addressSearch.open) return;

    const atGroupLevel = addressSearch.details.length === 0;   
    const onlyDirectGroup =
      addressSearch.groups.length === 1 &&
      addressSearch.groups[0].id === "__DIRECT_ADDR__";

    if (atGroupLevel && onlyDirectGroup) {
      addressSearch.drillInto(addressSearch.groups[0]);
    }
  }, [addressSearch.open, addressSearch.groups, addressSearch.details, addressSearch.drillInto, ]);

  const manualText =
  typeof manualValue === "string" ? manualValue : addressSearch.manualValueToShow;
  const handleManualChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    if (onManualChange) onManualChange(e.target.value);
    else addressSearch.handleManualTextareaChange(e);
  };

  const shouldShowSummary =
  !!showSummaryCard && (addressSearch.showSummary || !!(manualText && manualText.trim()));

  const endAdornment = addressSearch.isQuerying
    ? <Icon name="loader" size="xs" />
    : addressSearch.hasValue
      ? (
        <button
          type="button"
          aria-label={strings.clearAriaLabel}
          onClick={addressSearch.handleClear}
          className={styles.clearBtn}
        >
          <Icon name="xCircle" size="xs" />
        </button>
      )
      : null;

const isDirectPlaceholder =
  addressSearch.open &&
  addressSearch.details.length === 0 &&
  addressSearch.groups.length === 1 &&
  addressSearch.groups[0]?.id === "__DIRECT_ADDR__";

  return (
    <div
      ref={addressSearch.wrapRef}
      className={`${styles.wrap} ${addressSearch.manualMode ? styles.noGreyDisabled : ""} ${className ?? ""}`}
    >
      <div className={`${styles.inputAnchor} ${
        addressSearch.open && allowOpen && allowDropdown ? styles.inputAnchorOpen : ""
      }`}>
      <TextInput
        id={baseId}
        type="text"
        value={value}
        onChange={(e) => {
          unlock();                                 
          addressSearch.handleInputChange(e.target.value);
        }}
        onKeyDown={(e) => {
          unlock();                              
          addressSearch.onKeyDown(e as any);
        }}
        onFocus={unlock}                             
        placeholder={placeholder ?? strings.placeholder}
        disabled={disabled}           
        label={label}
        hintText={addressSearch.open ? undefined : subLabel}
        error={false}
        errorMessage={null}
        endAdornment={endAdornment}
        closeIcon={false}
        inputProps={{"aria-describedby":ariaDescribedBy}}
      />

      {process.env.NODE_ENV === "test" && (
      <div data-testid="dbg">
        open={String(addressSearch.open)}
        groups={addressSearch.groups.length}
        level={addressSearch.level}
        manual={String(addressSearch.manualMode)}
        summary={String(addressSearch.showSummary)}
      </div>
    )}


      {addressSearch.open && allowOpen && allowDropdown && !isDirectPlaceholder && (
        <ResultsPanel
          level={addressSearch.level}
          groups={addressSearch.groups}
          details={addressSearch.details}
          highlight={addressSearch.highlight}
          onHover={addressSearch.setHighlight}
          onSelectGroup={addressSearch.drillInto}
          onSelectDetail={(d) => addressSearch.selectDetail(d)}
          onEnterManual={
          props.onEnterManual
          ? () => {
              addressSearch.enterManual();
              props.onEnterManual?.();
            }
          : undefined
          }
          query={value}
          strings={strings}
        />
      )}
      </div>

      {shouldShowSummary && (
      <div className={styles.summaryBlock}>
        <div id={`${baseId}-summary-title`}>
          <Typography component="p" variant="text" size="sm" weight="semibold" className={styles.summaryLabel}>
            {strings.summaryTitle}
          </Typography>
        </div>

        <textarea
          ref={addressSearch.manualRef}
          className={styles.summaryTextarea}
          value={manualText}
          onChange={handleManualChange}
          onKeyDown={addressSearch.handleManualTextareaKeyDown}
          rows={4}
          aria-labelledby={`${baseId}-summary-title`}
        />
      </div>
    )}
    </div>
  );
}